import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  MapPinIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  ScaleIcon,
  GlobeAltIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  EnvelopeIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  DocumentArrowDownIcon,
  GlobeAmericasIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const howItWorksTogether = [
  {
    icon: MapPinIcon,
    title: 'Clarid Identifies CRA Gaps by Assessment Area',
    description:
      'Clarid analyzes lending, investment, and service activity across every delineated assessment area — surfacing exactly where a bank is falling short on CRA performance. Tract-level specificity shows which geographies need more community development investment, which income levels are underserved, and where lending volume gaps exist.',
  },
  {
    icon: BanknotesIcon,
    title: 'Raymond James Matches Qualified Investments to Those AAs',
    description:
      'With precise gap data in hand, Raymond James recommends CRA-qualified investments and loans that map directly to the bank\'s underperforming assessment areas. No more guessing which products will move the needle — every investment is targeted to where the bank actually needs credit.',
  },
  {
    icon: ChartBarIcon,
    title: 'Real-Time Impact on CRA Exam Readiness',
    description:
      'As qualified investments are deployed, Clarid\'s CRA module reflects the updated position in real time. Banks can watch their CRA health score, assessment area coverage, and exam readiness metrics improve — with full visibility into how each investment contributes to the overall CRA picture.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'Board and Exam Packaging Shows the Full Picture',
    description:
      'Every investment flows into Clarid\'s cross-module reporting. Board reports pull CRA health scores, community development activity, and assessment area performance into a single view. Exam packages include the complete narrative — gap identification, investment strategy, and measurable outcomes — ready for examiner review.',
  },
];

const platformCapabilities = [
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Module',
    description:
      'Assessment area delineation with census tract demographics. Lending Test analysis pulling from HMDA, small business, small farm, and consumer origination data. Community development activity tracking across loans, investments, grants, and services. CRA health scoring, gap analysis for underserved geographies, and exam readiness assessment.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Full IFLEP-aligned Fair Lending Dashboard with 17 risk factors across underwriting, pricing, steering, redlining, and marketing. Denial rate and pricing disparity analysis with statistical testing. Census tract mapping with demographic overlays and majority-minority tract classification.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'Validate, clean, and prepare LAR data for submission. 170+ CFPB edit checks across syntactical, validity, quality, and macro-quality categories. Field mapping templates for LOS imports. Data quality scoring, geocoding validation, and submission-ready exports.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA examination standards.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of enforcement actions and regulatory updates from CFPB, OCC, and FDIC. Enforcement scraping with deduplication, high-value action flagging, compliance area tagging, and priority assignment.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Exam & Board Packaging',
    description:
      'One-click generation of examiner-ready ZIP exports and executive board reports. Every module\'s findings — CRA, fair lending, HMDA, regression, marketing compliance — aggregated into unified packages with full methodology documentation and audit trails.',
  },
];

const integrationSteps = [
  {
    num: 1,
    title: 'Bank Builds Assessment Areas in Clarid',
    description:
      'The bank delineates its CRA assessment areas in Clarid\'s CRA module, mapping census tracts, demographics, income levels, and geographic boundaries. This creates the foundation for all gap analysis.',
  },
  {
    num: 2,
    title: 'Clarid Surfaces CRA Gaps with Tract-Level Specificity',
    description:
      'Clarid analyzes lending volumes, community development activity, and geographic distribution against assessment area demographics. The result: a precise map of where the bank is underperforming — by geography, income level, and CRA test category.',
  },
  {
    num: 3,
    title: 'Raymond James Recommends Qualified Investments Targeting Those AAs',
    description:
      'Armed with Clarid\'s gap data, Raymond James identifies CRA-qualified investments and loans that directly address the bank\'s specific underperforming assessment areas. Every recommendation is mapped to a real gap.',
  },
  {
    num: 4,
    title: 'Investment Data Flows Into Clarid\'s CRA Module',
    description:
      'As the bank deploys qualified investments, the activity is recorded in Clarid\'s community development tracking. CRA health scores, assessment area coverage metrics, and exam readiness indicators update to reflect the improved position.',
  },
  {
    num: 5,
    title: 'Board Reports and Exam Packages Reflect the Improved CRA Position',
    description:
      'Clarid\'s cross-module reporting pulls the full narrative together — the gaps identified, the investments deployed, and the measurable impact on CRA performance. Board reports and exam packages tell a complete, defensible story.',
  },
];

const bondModuleCapabilities = [
  {
    icon: CurrencyDollarIcon,
    title: 'CUSIP-Level CRA Qualification',
    description:
      'Paste any mix of Freddie Mac and Fannie Mae CUSIPs. Clarid pulls Freddie OCS prospectus data and Fannie DUS Disclose detail reports, geocodes every underlying property, matches each to the bank\'s delineated assessment areas, and returns per-CUSIP qualified dollar totals with full per-property breakdowns — in seconds.',
  },
  {
    icon: SparklesIcon,
    title: 'OpenFIGI Auto-Resolution',
    description:
      'Analysts rarely know the internal Deal ID behind a CUSIP. Clarid calls OpenFIGI in the background to auto-map unknown CUSIPs to their Freddie deals — no manual lookup required. The result table flags every auto-resolved bond so the analyst sees the assist happened.',
  },
  {
    icon: MapPinIcon,
    title: 'Assessment Area Matching',
    description:
      'Every property\'s census tract is geocoded and overlaid on the bank\'s exact assessment area boundaries. Properties are classified as inside-AA or outside-AA with the matching assessment area name. LMI unit percentages are computed and surfaced at both the property and portfolio level.',
  },
  {
    icon: GlobeAmericasIcon,
    title: 'Portfolio Map View',
    description:
      'The "demo closer" — a geographic map of every bond\'s underlying properties color-coded by qualification status: qualifies (green), outside assessment area (gray), no affordability data (amber), geocoding failure (red). Analysts can filter by status, state, assessment area, and minimum LMI percentage, with the map and table staying in sync.',
  },
  {
    icon: DocumentArrowDownIcon,
    title: 'Pitch PDF Generation',
    description:
      'One click generates a bank-facing Pitch PDF across the full analyzed CUSIP set — qualified dollars, property counts, assessment area coverage, and LMI composition. The PDF is ready to share with the bank\'s CRA officer or board without any post-processing.',
  },
  {
    icon: CheckCircleIcon,
    title: 'Fannie + Freddie Coverage',
    description:
      'Freddie Multi PCs are analyzed via MSIA Property Detail CSV upload plus OCS prospectus extraction. Fannie MBS bonds are analyzed via DUS Disclose Detail Report xlsx upload. Both sources land in the same analysis surface — analysts see a single qualification table regardless of GSE.',
  },
];

const bondWorkflowSteps = [
  {
    num: 1,
    title: 'Upload Source Data',
    description:
      'Drop a Freddie MSIA Property Detail CSV (per deal, from MSIA Standard Reports) and/or a Fannie DUS Disclose Detail Report xlsx (per security). Re-uploads update existing records. The bank\'s CRA assessment areas are already in Clarid from the CRA module.',
  },
  {
    num: 2,
    title: 'Paste CUSIPs',
    description:
      'One CUSIP per line or comma-separated. Unknown CUSIPs are auto-mapped to their deals via OpenFIGI — no manual Deal ID lookup. Clarid then pulls the OCS prospectus (Freddie) or DUS Disclose data (Fannie), geocodes every underlying property, and computes CRA qualification against the bank\'s assessment areas.',
  },
  {
    num: 3,
    title: 'Review Results',
    description:
      'Instant table: qualified dollar total, properties inside vs. outside assessment area, geocoding failures, and affordability data gaps — per CUSIP. Click any row for a per-property drill-down with census tract, LMI percentage, AA match, and allocated dollars. Switch to Map view to see the portfolio visually.',
  },
  {
    num: 4,
    title: 'Generate Pitch PDF',
    description:
      'One click exports a bank-ready Pitch PDF across all analyzed CUSIPs — qualified totals, property-level detail, assessment area coverage, and LMI composition. Hand it to the bank\'s CRA officer or drop it into the board package. Clarid\'s CRA module tracks the deployment and reflects it in exam readiness automatically.',
  },
];

/* ─── Component ─── */
export default function RaymondJamesPartnerPage() {
  useEffect(() => {
    document.title = 'Raymond James + Clarid — Bond Analysis & CRA Intelligence';

    const metaTags: Record<string, string> = {
      'og:title': 'Raymond James + Clarid — Bond Analysis & CRA Intelligence',
      'og:description': 'Purpose-built bond analysis module for CRA-qualified investments. CUSIP-level qualification, assessment area matching, and pitch PDF generation — built for Raymond James analysts.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Raymond James + Clarid Partnership',
      'twitter:description': 'CRA intelligence meets qualified investment solutions.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/raymond-james-logo.svg" alt="Raymond James" className="h-10" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 mb-8">
              Strategic Partnership
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-6xl mx-auto">
              CRA Intelligence Meets{' '}
              <span className="text-indigo-600">Qualified Investment</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed mb-10">
              Clarid identifies exactly where a bank's CRA performance falls short — by assessment area,
              product, census tract, and income level. Raymond James provides the CRA-qualified
              investments and loans that fill those gaps. Together — precision analytics connected
              to targeted investment and lending solutions that actually move the needle.
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="inline-flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-2xl px-6 py-4">
              <BuildingOffice2Icon className="h-6 w-6 text-indigo-600 flex-shrink-0" />
              <p className="text-base md:text-lg font-semibold text-indigo-900">
                Now includes the Bond Analysis Module — purpose-built for Raymond James analysts
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Opportunity ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The CRA Investment Challenge
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks know they need CRA-qualified investments to perform well on CRA
                  examinations. But the challenge isn't finding investment products — it's finding
                  the right investments that map to their specific assessment areas and actually
                  improve their CRA position where it matters.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Generic investment products don't account for a bank's delineated assessment areas,
                  the demographic composition of specific census tracts, or the gap between current
                  activity and what examiners expect. A bank might deploy capital into investments
                  that look good on paper but don't address the geographies where they're actually
                  underperforming.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  The result: banks invest in CRA-qualified products without clear visibility into
                  whether those investments are moving their CRA exam readiness score in the right
                  direction — or whether they're leaving their most critical assessment area gaps
                  unaddressed.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Clarid provides the intelligence. Raymond James provides the investment. The bank gets a CRA strategy that's data-driven from gap to close.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Bond Analysis Module Feature Highlight ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-center text-indigo-600 mb-5">
              Built for Raymond James
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              The Bond Analysis Module
            </h2>
            <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-5xl mx-auto mb-16 leading-relaxed">
              A purpose-built analyst tool that takes Raymond James bond CUSIPs, maps every
              underlying property to a bank's delineated assessment areas, computes CRA-qualified
              dollar totals, and generates a client-ready Pitch PDF — in one workflow. Freddie
              Mac Multi PCs and Fannie Mae MBS bonds in the same surface.
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {bondModuleCapabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.08}>
                <div className="group p-8 lg:p-10 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300 mb-5">
                    <cap.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-display font-bold text-navy-900 mb-3">{cap.title}</h3>
                  <p className="text-base text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 4: Bond Module Workflow ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-center text-indigo-300 mb-5">
              The Analyst Workflow
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white text-center mb-6">
              From CUSIP to Pitch PDF in Four Steps
            </h2>
            <p className="text-xl md:text-[1.35rem] text-slate-400 text-center max-w-5xl mx-auto mb-16 leading-relaxed">
              No spreadsheets, no manual Deal ID lookups, no geocoding by hand. The entire
              workflow — data ingestion, qualification analysis, map review, and client deliverable
              — lives inside Clarid.
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto space-y-10 lg:space-y-12">
            {bondWorkflowSteps.map((step, i) => (
              <FadeUp key={step.num} delay={i * 0.12}>
                <div className="flex items-start gap-7">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold">
                    {step.num}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-lg text-slate-400 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.5}>
            <div className="mt-16 max-w-4xl mx-auto p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10">
              <p className="text-lg text-slate-300 leading-relaxed text-center">
                The Pitch PDF and CSV exports are designed for the bank's CRA officer and board —
                not for internal analyst use. Raymond James walks in with a document that shows
                exactly which bonds qualify, how much, and why. The bank can hand it to examiners.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 5: How Clarid + RJ Work Together ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                How Clarid + Raymond James Work Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                A closed-loop workflow: identify the gap, deploy the investment, measure the impact,
                and package the story for examiners and the board.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {howItWorksTogether.map((item, i) => (
              <ScaleIn key={item.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <item.icon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{item.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{item.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 4: Platform Capabilities ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-300 mb-5">
                The Platform
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                What Clarid Does
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                An AI-native compliance operating system purpose-built for community banks. CRA is one
                module in a fully interconnected platform — every finding, every data point, every
                investment flows into exam packages and board reports automatically.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {platformCapabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-3">
                    <cap.icon className="h-6 w-6 text-indigo-400 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.5}>
            <p className="mt-14 text-center text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
              Clarid's cross-module interconnectivity means CRA gap data doesn't live in isolation.
              It connects to fair lending risk assessments, HMDA data quality, regression findings,
              and board-level reporting — giving the bank and its investment partners a complete picture.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 5: Integration Vision ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                The Integration Vision
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                A five-step workflow that connects CRA intelligence to qualified investment — and
                closes the loop with measurable outcomes in exam-ready and board-ready formats.
              </p>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {integrationSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                The end state: banks don't just check the CRA box — they have a data-driven investment
                strategy with documented impact, ready for examiners and the board.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 6: Built With the Examination Perspective ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert — with decades of combined
                  examination experience across hundreds of community bank examinations — are
                  partners in Clarid. Every CRA module output, every gap analysis, every exam
                  package is validated against what examiners actually evaluate.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community bank CMO who manages CRA reporting, HMDA
                  submissions, and examiner relationships as part of his daily work. This platform
                  wasn't designed by engineers guessing at compliance needs — it was built by someone
                  who lives them and who will sit across from examiners using the outputs Clarid
                  generates.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Contact ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-indigo-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-indigo-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/raymond-james-logo.svg" alt="Raymond James" className="h-8 opacity-40" />
            <span className="text-slate-300 text-lg font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-8 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
